import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogoMark } from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../services/api";
import { ArrowRight, Lock } from "lucide-react";

const Login = () => {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user && user !== false) navigate("/portal", { replace: true });
    }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await login(email.trim(), password);
            const to = location.state?.from || "/portal";
            navigate(to.startsWith("/portal") ? to : "/portal", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="min-h-screen grid lg:grid-cols-2 bg-white"
            data-testid="login-page"
        >
            {/* Left visual panel */}
            <div className="hidden lg:flex relative ns-cross-pattern p-12 flex-col justify-between">
                <Link to="/" className="flex items-center gap-3">
                    <LogoMark size={36} />
                    <div className="leading-none">
                        <div className="font-display text-base font-semibold tracking-tight">
                            Nador Sage International
                        </div>
                        <div
                            className="font-body text-[10px] uppercase tracking-[0.22em] mt-1"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            S.r.l.
                        </div>
                    </div>
                </Link>

                <div className="max-w-md">
                    <span className="ns-eyebrow">Restricted Access</span>
                    <h1
                        className="font-display mt-6 text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Aerospace Procurement
                        <br />
                        &amp; RFQ Management Portal.
                    </h1>
                    <p
                        className="font-body mt-6 text-[15px] leading-relaxed"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        Centralised submission and tracking of aviation
                        component procurement requests. Authorised partners
                        only.
                    </p>
                </div>

                <div
                    className="font-body text-[11px] uppercase tracking-[0.22em]"
                    style={{ color: "var(--ns-muted)" }}
                >
                    © {new Date().getFullYear()} Nador Sage International S.r.l.
                </div>
            </div>

            {/* Right form panel */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-md"
                    data-testid="login-form"
                >
                    <Lock
                        size={22}
                        style={{ color: "var(--ns-ink)" }}
                        className="mb-6"
                    />
                    <h2
                        className="font-display text-3xl lg:text-4xl font-bold tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Sign in
                    </h2>
                    <p
                        className="font-body mt-3 text-sm"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        Access the RFQ Portal with your authorised credentials.
                    </p>

                    {error && (
                        <div
                            className="mt-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm"
                            style={{ color: "#7a2218" }}
                            data-testid="login-error"
                        >
                            {error}
                        </div>
                    )}

                    <div className="mt-8 space-y-5">
                        <Field
                            id="email"
                            type="email"
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                        />
                        <Field
                            id="password"
                            type="password"
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={busy}
                        className="mt-8 ns-btn-primary w-full justify-center"
                        data-testid="login-submit"
                    >
                        {busy ? "Signing in…" : "Sign in"}
                        <ArrowRight size={16} />
                    </button>

                    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
                        <Link
                            to="/portal/register"
                            className="font-body hover:underline"
                            style={{ color: "var(--ns-ink)" }}
                            data-testid="login-register-link"
                        >
                            Request access →
                        </Link>
                        <Link
                            to="/"
                            className="font-body"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            ← Back to site
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Field = ({ id, label, ...props }) => (
    <div>
        <label
            htmlFor={id}
            className="block font-body text-[11px] uppercase tracking-[0.22em] mb-2"
            style={{ color: "var(--ns-muted)" }}
        >
            {label}
        </label>
        <input
            id={id}
            name={id}
            {...props}
            className="w-full bg-white border border-[var(--ns-border)] px-4 py-3 font-body text-base focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
            style={{ color: "var(--ns-ink)" }}
        />
    </div>
);

export default Login;
